import { times } from "lodash/fp";
import React from "react";
import styled, { css } from "styled-components";
import IconSunLmDe from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconSunLmDe.js";
import IconSunLmDeHalf from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconSunLmDeHalf.js";
import ResponsiveTooltip from "@/components/UI/Tooltip/ResponsiveTooltip";

const sizeToPx = { small: "11px", medium: "1rem", large: "1.25rem" };

const commonStarStyle = css<{ $sizename: keyof typeof sizeToPx }>`
  margin-left: 1px;
  margin-right: 1px;
  width: ${(props) => sizeToPx[props.$sizename]};
  height: ${(props) => sizeToPx[props.$sizename]};
`;

const Star = styled(IconSunLmDe)<{ $sizename: keyof typeof sizeToPx }>`
  ${commonStarStyle};
`;

const StarHalf = styled(IconSunLmDeHalf)<{ $sizename: keyof typeof sizeToPx }>`
  ${commonStarStyle};
`;

const Container = styled.div<{ $colored: boolean }>(({ $colored }) => ({
  display: "flex",
  alignItems: "center",
  [`> ${Star}, > ${StarHalf}`]: {
    fill: $colored ? "#FFB511" : "white",
  },
}));

const StyledResponsiveTooltip = styled(ResponsiveTooltip)`
  display: inline-block;
`;

interface StarsProps {
  amount?: number;
  size?: keyof typeof sizeToPx;
  className?: string;
  colored?: boolean;
}

const Stars: React.FC<StarsProps> = ({
  amount = 0,
  size = "small",
  colored = true,
}) => {
  if (!amount) return null;

  const wholeStars = Math.floor(amount);
  const hasHalfStar = amount % 1 === 0.5;

  return (
    <Container
      role="img"
      aria-label={`${amount.toLocaleString()} Sonnen`}
      $colored={colored}
      data-testid="stars"
      data-tooltip
    >
      {times(
        (index) => (
          <Star key={index} $sizename={size} />
        ),
        wholeStars
      )}
      {hasHalfStar && <StarHalf key="halfStar" $sizename={size} />}
    </Container>
  );
};

const StarsWithTooltip: React.FC<StarsProps> = (props) => (
  <StyledResponsiveTooltip
    trigger={<Stars {...props} />}
    content="Diese Bewertung basiert auf einer Selbsteinschätzung der Unterkunft und verdeutlicht das Preis-Leistungs-Verhältnis, die Ausstattung und die Leistungen der Unterkunft gemäß der Hotel-Industriestandards."
  />
);

export { StarsWithTooltip };
export default Stars;
